import { useCallback, useEffect, useState } from "react";
import { Toast } from "primereact/toast";

import {
  getSetupFee,
  invoiceSetupFee,
  waiveSetupFee,
} from "../services/onboarding/setupFee";
import { SetupFee } from "../types/onboarding";
import { setupFeeErrorMessage } from "../pages/Onboarding/Prospects/components/setupFeeErrors";

// The dialog shows a refusal inline, next to the action that was refused, so a
// failure comes back as words rather than as a toast.
// `message` is set exactly when `ok` is false.
export interface SetupFeeActionResult {
  ok: boolean;
  message?: string;
}

interface UseProspectSetupFeeParams {
  id: string;
  toastRef: React.RefObject<Toast>;
  // False before promotion: the fee is NOT_DUE by definition, so nothing is
  // fetched and nothing is shown.
  enabled: boolean;
}

export interface UseProspectSetupFee {
  setupFee: SetupFee | null;
  loading: boolean;
  error: boolean;
  reload: () => Promise<void>;
  invoice: () => Promise<SetupFeeActionResult>;
  waive: (reason: string) => Promise<SetupFeeActionResult>;
}

/**
 * The onboarding setup fee (CWA-196): its state, and the two decisions an admin
 * can take on it. Its own fetch, like the station list, so deciding the fee does
 * not refetch the prospect.
 */
const useProspectSetupFee = ({
  id,
  toastRef,
  enabled,
}: UseProspectSetupFeeParams): UseProspectSetupFee => {
  const [setupFee, setSetupFee] = useState<SetupFee | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const reload = useCallback(async () => {
    if (!enabled) {
      setSetupFee(null);
      return;
    }
    setLoading(true);
    setError(false);
    try {
      setSetupFee(await getSetupFee(id));
    } catch (e) {
      console.error("Failed to load the setup fee", e);
      // A fee we could not re-read is no longer known: keep nothing stale on
      // screen and no action on it.
      setSetupFee(null);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [id, enabled]);

  useEffect(() => {
    reload();
  }, [reload]);

  const decide = useCallback(
    async (
      action: () => Promise<SetupFee>,
      successMessage: string
    ): Promise<SetupFeeActionResult> => {
      try {
        setSetupFee(await action());
        toastRef.current?.show({
          severity: "success",
          summary: "Success",
          detail: successMessage,
          life: 3000,
        });
        return { ok: true };
      } catch (e) {
        console.error("Setup fee decision failed", e);
        // A refusal can still have moved the fee — a timeout leaves it
        // INVOICING, a missing billing customer re-opens it — so re-read it.
        await reload();
        return { ok: false, message: setupFeeErrorMessage(e) };
      }
    },
    [reload, toastRef]
  );

  const invoice = useCallback(
    () =>
      decide(
        () => invoiceSetupFee(id),
        "Draft invoice created in Easybill."
      ),
    [decide, id]
  );

  const waive = useCallback(
    (reason: string) =>
      decide(() => waiveSetupFee(id, reason), "Setup fee waived."),
    [decide, id]
  );

  return { setupFee, loading, error, reload, invoice, waive };
};

export default useProspectSetupFee;
